import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Alert,
  Paper,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Chip,
} from '@mui/material';
import {
  BarChartOutlined as BarChartIcon,
  ShowChartOutlined as LineChartIcon,
  PieChartOutline as PieChartIcon,
  TimelineOutlined as AreaChartIcon,
  SearchOutlined as SearchIcon,
  CloseRounded as CloseIcon,
  DashboardOutlined as DashboardIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDashboardStore } from '../stores/dashboard.store';
import { useOrganizationStore } from '../stores/organization.store';
import { DashboardStatistics, StatisticType } from '../types/dashboard.type';
import StatCard from '../components/dashboard/StatCard';
import ChartCard from '../components/dashboard/ChartCard';
import EsgRecycledWasteByTypeChart from '../components/dashboard/EsgRecycledWasteByTypeChart';
import CarbonAvoidedByStreamChart from '../components/dashboard/CarbonAvoidedByStreamChart';
import CarbonAvoidanceDetailView from '../components/dashboard/CarbonAvoidanceDetailView';
import KPILimitsDialog from '../components/dashboard/KPILimitsDialog';
import { format, isValid, parse, parseISO, startOfDay, startOfMonth } from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import KPIProgressCard from '../components/dashboard/KPIProgressCard';
import BranchSiteCard from '../components/dashboard/BranchSiteCard';
import BranchComparisonDialog from '../components/dashboard/BranchComparisonDialog';
import DashboardSkeleton from '../components/dashboard/DashboardSkeleton';
import PageHeader from '../components/PageHeader';
import ScopeEmissionsPanel from '../components/data-entry/ScopeEmissionsPanel';
import EmptyState from '../components/EmptyState';
import KpiFigure, { type KpiFigureProps } from '../components/metrics/KpiFigure';
import MetricStrip from '../components/metrics/MetricStrip';
import EsgSection from '../components/dashboard/sections/EsgSection';
import HseDashboardSection from '../components/dashboard/HseDashboardSection';
import PerformanceHero from '../components/dashboard/sections/PerformanceHero';
import { groupMonthlyCards, overviewWidgetsIn } from '../components/dashboard/esgMetricGroups';
import { ESG_CONTENT_SHAPE } from '../constants/colors';
import { ESG_ICONS } from '../constants/esgIcons';
import UnfoldLessRounded from '@mui/icons-material/UnfoldLessRounded';
import UnfoldMoreRounded from '@mui/icons-material/UnfoldMoreRounded';
import { isTrackableKpi, kpiTargetValue } from '../constants/kpiTargets';
import { useNumberFormat } from '../hooks/useNumberFormat';
import { ESG_METRIC_POLARITY, operationalStatPolarity } from '../utils/metricPolarity';
import { numberLocale, type MetricValueKind } from '../utils/numberFormat';
import { SetupCoachmark } from '../components/selfSetup/SetupCoachmark';
import {
  PAGE_HEADER_TABS_ROW_SX,
  PAGE_HEADER_TABS_SX,
} from '../constants/page-header';
import { useSelfSetupTour } from '../hooks/useSelfSetupTour';
import {
  HELP_GUIDE_CUSTOMIZE,
  parseHelpDashboardGuide,
  type HelpDashboardGuideKind,
} from '../constants/knowledgeBaseTryIt';
import { helpI18n } from '../i18n/help';
import {
  clearHelpGuideActive,
  markHelpGuideActive,
} from '../utils/helpGuideSession';
import { useKPIStore } from '../stores/kpi.store';
import { useEsgDashboardStore } from '../stores/esg-dashboard.store';
import { useCarbonAvoidanceReportStore } from '../stores/carbon-avoidance-report.store';
import {
  ESG_MONTHLY_NON_OPERATIONAL_KEYS,
  type CarbonDashboardScalars,
  type EsgDashboardMonthlyResponse,
  type EsgDashboardOverviewResponse,
} from '../types/esg-dashboard.type';
import {
  DEFAULT_ESG_MONTHLY_METRICS,
  ESG_MONTHLY_SUMMARY_METRIC_ORDER,
  ESG_MONTHLY_UTILITY_METRIC_ORDER,
  EsgMonthlyMetricType,
  isEsgMonthlyChartMetric,
  PICKABLE_ESG_MONTHLY_METRICS,
} from '../types/esg-monthly-metric.type';
import {
  DEFAULT_ESG_OVERVIEW_WIDGETS,
  type EsgOverviewWidgetId,
  PICKABLE_ESG_OVERVIEW_WIDGETS,
} from '../types/esg-overview-widget.type';
import {
  DEFAULT_ESG_BRANCHES_WIDGETS,
  DEFAULT_ESG_CARBON_WIDGETS,
  DEFAULT_ESG_MONTHLY_EXTRA_WIDGETS,
  DEFAULT_ESG_SCOPES_WIDGETS,
  ESG_BRANCHES_WIDGETS,
  ESG_CARBON_WIDGETS,
  ESG_MONTHLY_EXTRA_WIDGETS,
  ESG_SCOPES_WIDGETS,
  type EsgBranchesWidgetId,
  type EsgCarbonWidgetId,
  type EsgMonthlyExtraWidgetId,
  type EsgScopesWidgetId,
} from '../types/dashboard-customize.type';
import {
  expandLegacyScopeEmissions,
  loadOperationalStatsForOrg,
  persistOperationalStatsForOrg,
  sanitizePickableIds,
} from '../utils/dashboardCustomizePreferences';
import DashboardCustomizeWidgetList from '../components/dashboard/DashboardCustomizeWidgetList';
import {
  getOperationalStatsForOrgType,
  isOperationalChartStat,
} from '../constants/dashboardStats.catalog';
import {
  getEsgMonthlyMetricLabel,
  getOverviewWidgetLabel,
  getStatisticLabel,
} from '../helpers/metric-i18n.helper';
import {
  carbonReportPeriodToParams,
  formatReportPeriodLabel,
  isCarbonReportPeriodValid,
  calculationWarningKey,
  pickTotalAvoidedTco2e,
  readCarbonReportPeriodFromSession,
  type CarbonReportPeriod,
  writeCarbonReportPeriodToSession,
} from '../utils/carbonAvoidance.util';

function parseDashboardMonth(iso: string): Date {
  if (!iso) return startOfMonth(new Date());
  if (/^\d{4}-\d{2}$/.test(iso)) {
    const d = parse(`${iso}-01`, 'yyyy-MM-dd', new Date());
    return isValid(d) ? startOfMonth(d) : startOfMonth(new Date());
  }
  const d = parseISO(iso);
  return isValid(d) ? startOfMonth(d) : startOfMonth(new Date());
}

const OVERVIEW_SUMMARY_WIDGETS: EsgOverviewWidgetId[] = [
  'summary_waste_diverted',
  'summary_fill_reports',
  'summary_diversion_rate',
  'summary_total_co2',
  'summary_training_hours',
];

const OVERVIEW_UTILITY_WIDGETS: EsgOverviewWidgetId[] = [
  'utility_water',
  'utility_electricity',
  'utility_oil',
  'utility_gas',
];

enum DashboardSection {
  MONTHLY = 0,
  OVERVIEW = 1,
  SCOPES = 2,
  BRANCHES = 3,
  CARBON_AVOIDANCE = 4,
}

/** Map legacy session keys (split utilities / prior-month widgets) to current picklist. */
const LEGACY_ESG_MONTHLY_METRIC_KEY: Record<string, EsgMonthlyMetricType> = {
  water_consumption: EsgMonthlyMetricType.WATER_USE,
  water_amount_sar: EsgMonthlyMetricType.WATER_USE,
  electricity_consumption_kwh: EsgMonthlyMetricType.ELECTRICITY_USE,
  electricity_amount_sar: EsgMonthlyMetricType.ELECTRICITY_USE,
  oil_consumption_liters: EsgMonthlyMetricType.OIL_USE,
  oil_amount_sar: EsgMonthlyMetricType.OIL_USE,
  gas_consumption_m3: EsgMonthlyMetricType.GAS_USE,
  gas_amount_sar: EsgMonthlyMetricType.GAS_USE,
};

function calcMomPercent(
  current: number,
  previous: number | null | undefined
): number {
  if (previous === null || previous === undefined || previous === 0) return 0;
  return ((current - previous) / previous) * 100;
}

/** Month-over-month % for ESG monthly StatCards (pairs from ESG_DASHBOARD_API.md). */
function esgMonthlyMetricMoM(
  esg: EsgDashboardMonthlyResponse,
  metric: EsgMonthlyMetricType
): { trend: number; hideTrend: boolean } {
  if (isEsgMonthlyChartMetric(metric)) {
    return { trend: 0, hideTrend: true };
  }

  switch (metric) {
    case EsgMonthlyMetricType.WASTE_DIVERSION_RATE:
      return {
        trend: calcMomPercent(
          esg.waste_diversion_rate_percent,
          esg.previous_month_waste_diversion_rate
        ),
        hideTrend: false,
      };
    case EsgMonthlyMetricType.TOTAL_CO2:
      return { trend: 0, hideTrend: true };
    case EsgMonthlyMetricType.TOTAL_WASTE_DIVERTED:
      return { trend: 0, hideTrend: false };
    case EsgMonthlyMetricType.FILL_LEVEL_REPORT_COUNT:
      return {
        trend: calcMomPercent(
          esg.fill_level_report_count,
          esg.previous_month_fill_level_report_count
        ),
        hideTrend: false,
      };
    case EsgMonthlyMetricType.TRAINING_HOURS_TOTAL: {
      if (esg.training_hours_total === null) return { trend: 0, hideTrend: true };
      return {
        trend: calcMomPercent(
          esg.training_hours_total,
          esg.previous_month_training_hours_total
        ),
        hideTrend: false,
      };
    }
    case EsgMonthlyMetricType.WATER_USE:
      return {
        trend: calcMomPercent(esg.water_consumption, esg.previous_month_water_consumption),
        hideTrend: false,
      };
    case EsgMonthlyMetricType.ELECTRICITY_USE:
      return {
        trend: calcMomPercent(
          esg.electricity_consumption_kwh,
          esg.previous_month_electricity_consumption_kwh
        ),
        hideTrend: false,
      };
    case EsgMonthlyMetricType.OIL_USE:
      return {
        trend: calcMomPercent(
          esg.oil_consumption_liters,
          esg.previous_month_oil_consumption_liters
        ),
        hideTrend: false,
      };
    case EsgMonthlyMetricType.GAS_USE:
      return {
        trend: calcMomPercent(esg.gas_consumption_m3, esg.previous_month_gas_consumption_m3),
        hideTrend: false,
      };
    default:
      return { trend: 0, hideTrend: false };
  }
}

/**
 * Everything a carbon total leaves out, phrased so the number can explain itself.
 *
 * Skipped runs were the only thing surfaced before, which made a zero total look the
 * same whether nothing was collected or every calculation was failing for want of an
 * emission factor. `missing` is usually the big one: completed pickups that produced
 * no calculation at all.
 */
function uncountedPickupsNote(
  source: Partial<CarbonDashboardScalars>,
  t: (key: string, options?: Record<string, unknown>) => string
): string | null {
  const skipped = source.calculation_skipped_count ?? 0;
  const failed = source.calculation_failed_count ?? 0;
  const missing = source.calculation_missing_count ?? 0;
  const total = skipped + failed + missing;
  if (total === 0) return null;

  const parts: string[] = [];
  if (missing > 0) parts.push(t('dashboard.alerts.uncountedNoCalculation', { count: missing }));
  if (failed > 0) parts.push(t('dashboard.alerts.uncountedFailed', { count: failed }));
  if (skipped > 0) parts.push(t('dashboard.alerts.uncountedSkipped', { count: skipped }));

  const topWarning = source.calculation_warnings?.[0];
  const breakdown = parts.join(', ');
  const reasonKey = calculationWarningKey(topWarning?.warning_code);
  return topWarning
    ? t('dashboard.alerts.ordersNotCountedWithReason', {
        count: total,
        breakdown,
        reason: reasonKey ? t(reasonKey) : topWarning.warning_code,
      })
    : t('dashboard.alerts.ordersNotCounted', { count: total, breakdown });
}

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const fmt = useNumberFormat();
  const { t: tHelp } = useTranslation('translation', { i18n: helpI18n });
  const navigate = useNavigate();
  const location = useLocation();
  const { isStep, completeStep, dismissTour, setTourPhase, tourPhase } =
    useSelfSetupTour();
  const showDashboardTour = isStep('dashboard');
  const [helpCustomizeGuide, setHelpCustomizeGuide] = useState(false);
  const [helpDashGuide, setHelpDashGuide] =
    useState<HelpDashboardGuideKind | null>(null);
  /** Self-setup page tips start after the sidebar nav tip. */
  const showOverviewTour =
    (showDashboardTour && tourPhase !== 'nav') || helpDashGuide != null;
  /** Knowledge-base customize guide only (opens the customize dialog). */
  const activeCustomizeGuide = helpCustomizeGuide;
  type OverviewTourPhase =
    | 'monthlyTab'
    | 'month'
    | 'customize'
    | 'customizeSearch'
    | 'customizeMetrics'
    | 'customizeSave'
    | 'kpi'
    | 'kpiForm'
    | 'kpiList'
    | 'esgCards'
    | 'charts'
    | 'branchesTab'
    | 'branches'
    | 'compareBranches'
    | 'compareDialog'
    | 'compareResults'
    | 'carbonTab'
    | 'carbonDate'
    | 'carbonContent';
  type CustomizeDialogPhase = 'button' | 'search' | 'metrics' | 'save';
  const [overviewTourPhase, setOverviewTourPhase] =
    useState<OverviewTourPhase>('monthlyTab');
  const [customizeTourPhase, setCustomizeTourPhase] =
    useState<CustomizeDialogPhase>('button');
  const { selectedOrganization } = useOrganizationStore();
  const orgType = selectedOrganization?.organization_type ?? null;
  const operationalStatsForOrg = useMemo(
    () => (orgType ? getOperationalStatsForOrgType(orgType) : []),
    [orgType],
  );
  const {
    selectedStats,
    selectedMonth,
    setSelectedStats,
    setSelectedMonth,
    loadDashboardPreferences,
  } = useDashboardStore();
  const {
    monthly: esgMonthly,
    overview: esgOverview,
    byBranch: esgByBranch,
    isLoading: esgLoading,
    error: esgError,
    fetchEsgDashboard,
  } = useEsgDashboardStore();
  const {
    report: carbonReport,
    isLoading: carbonLoading,
    error: carbonError,
    fetchCarbonAvoidanceReport,
    reset: resetCarbonReport,
  } = useCarbonAvoidanceReportStore();

  const { limits } = useKPIStore();
  const hasEnabledKPIs = limits.some((limit) => limit.isEnabled);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedStat, setSelectedStat] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openKPIDialog, setOpenKPIDialog] = useState(false);
  const [openBranchCompare, setOpenBranchCompare] = useState(false);
  const [expandedBranches, setExpandedBranches] = useState<ReadonlySet<string>>(() => new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [chartTypes, setChartTypes] = useState<Record<string, 'bar' | 'line' | 'area' | 'pie'>>({});
  const [dashboardTab, setDashboardTab] = useState<DashboardSection>(
    DashboardSection.MONTHLY
  );
  const [selectedEsgMonthlyMetrics, setSelectedEsgMonthlyMetrics] = useState<EsgMonthlyMetricType[]>(
    DEFAULT_ESG_MONTHLY_METRICS
  );
  const [selectedOverviewWidgets, setSelectedOverviewWidgets] = useState<EsgOverviewWidgetId[]>(
    DEFAULT_ESG_OVERVIEW_WIDGETS
  );
  const [selectedMonthlyExtras, setSelectedMonthlyExtras] = useState<EsgMonthlyExtraWidgetId[]>(
    DEFAULT_ESG_MONTHLY_EXTRA_WIDGETS
  );
  const [selectedScopesWidgets, setSelectedScopesWidgets] = useState<EsgScopesWidgetId[]>(
    DEFAULT_ESG_SCOPES_WIDGETS
  );
  const [selectedBranchesWidgets, setSelectedBranchesWidgets] = useState<EsgBranchesWidgetId[]>(
    DEFAULT_ESG_BRANCHES_WIDGETS
  );
  const [selectedCarbonWidgets, setSelectedCarbonWidgets] = useState<EsgCarbonWidgetId[]>(
    DEFAULT_ESG_CARBON_WIDGETS
  );
  const [carbonPeriod, setCarbonPeriod] = useState<CarbonReportPeriod>(() =>
    readCarbonReportPeriodFromSession()
  );

  const showMonthlyTabTip = showOverviewTour && overviewTourPhase === 'monthlyTab';
  const showMonthTip = showOverviewTour && overviewTourPhase === 'month';
  const showKpiTip = showOverviewTour && overviewTourPhase === 'kpi';
  const showOverviewCustomizeTip =
    showOverviewTour && overviewTourPhase === 'customize';
  const showEsgCardsTip = showOverviewTour && overviewTourPhase === 'esgCards';
  const showChartsTip = showOverviewTour && overviewTourPhase === 'charts';
  const showBranchesTabTip =
    showOverviewTour && overviewTourPhase === 'branchesTab';
  const showBranchesTip = showOverviewTour && overviewTourPhase === 'branches';
  const showCompareTip =
    showOverviewTour && overviewTourPhase === 'compareBranches';
  const showCompareDialogTip =
    showOverviewTour && overviewTourPhase === 'compareDialog';
  const showCompareResultsTip =
    showOverviewTour && overviewTourPhase === 'compareResults';
  const showCompareTourDialog =
    showCompareDialogTip || showCompareResultsTip;
  const showCarbonTabTip = showOverviewTour && overviewTourPhase === 'carbonTab';
  const showCarbonDateTip =
    showOverviewTour && overviewTourPhase === 'carbonDate';
  const showCarbonContentTip =
    showOverviewTour && overviewTourPhase === 'carbonContent';

  const showCustomizeButtonTip =
    (activeCustomizeGuide && customizeTourPhase === 'button') ||
    showOverviewCustomizeTip;
  const showCustomizeSearchTip =
    (activeCustomizeGuide && customizeTourPhase === 'search') ||
    (showOverviewTour && overviewTourPhase === 'customizeSearch');
  const showCustomizeMetricsTip =
    (activeCustomizeGuide && customizeTourPhase === 'metrics') ||
    (showOverviewTour && overviewTourPhase === 'customizeMetrics');
  const showCustomizeSaveTip =
    (activeCustomizeGuide && customizeTourPhase === 'save') ||
    (showOverviewTour && overviewTourPhase === 'customizeSave');
  const showKpiFormTip = showOverviewTour && overviewTourPhase === 'kpiForm';
  const showKpiListTip = showOverviewTour && overviewTourPhase === 'kpiList';

  const syncPhase = (phase: OverviewTourPhase) => {
    setOverviewTourPhase(phase);
    if (showDashboardTour) setTourPhase(phase);
  };

  const finishDashboardGuide = () => {
    setOpenDialog(false);
    setOpenKPIDialog(false);
    setOpenBranchCompare(false);
    setOverviewTourPhase('monthlyTab');
    setCustomizeTourPhase('button');
    if (helpCustomizeGuide) {
      setHelpCustomizeGuide(false);
      clearHelpGuideActive();
      return;
    }
    if (helpDashGuide) {
      setHelpDashGuide(null);
      clearHelpGuideActive();
      if (showDashboardTour) {
        completeStep('dashboard');
      }
      return;
    }
    completeStep('dashboard');
  };

  /** Soft-dismiss (X): Later for self-setup — do not mark the step done. */
  const softDismissDashboardTour = () => {
    setOpenDialog(false);
    setOpenKPIDialog(false);
    setOpenBranchCompare(false);
    if (helpCustomizeGuide) {
      setHelpCustomizeGuide(false);
      clearHelpGuideActive();
      setCustomizeTourPhase('button');
      return;
    }
    if (helpDashGuide) {
      setHelpDashGuide(null);
      clearHelpGuideActive();
      setOverviewTourPhase('monthlyTab');
      return;
    }
    dismissTour(overviewTourPhase);
  };

  // Resume self-setup tip from stored spot after Continue / re-login.
  useEffect(() => {
    if (!showDashboardTour) return;
    if (tourPhase === 'nav') return;
    const phase = tourPhase;
    const known: OverviewTourPhase[] = [
      'monthlyTab',
      'month',
      'customize',
      'customizeSearch',
      'customizeMetrics',
      'customizeSave',
      'kpi',
      'kpiForm',
      'kpiList',
      'esgCards',
      'charts',
      'branchesTab',
      'branches',
      'compareBranches',
      'compareDialog',
      'compareResults',
      'carbonTab',
      'carbonDate',
      'carbonContent',
    ];
    if (known.includes(phase as OverviewTourPhase)) {
      setOverviewTourPhase(phase as OverviewTourPhase);
      if (
        phase === 'branchesTab' ||
        phase === 'branches' ||
        phase === 'compareBranches' ||
        phase === 'compareDialog' ||
        phase === 'compareResults'
      ) {
        setDashboardTab(DashboardSection.BRANCHES);
      } else if (
        phase === 'carbonTab' ||
        phase === 'carbonDate' ||
        phase === 'carbonContent'
      ) {
        setDashboardTab(DashboardSection.CARBON_AVOIDANCE);
      } else {
        setDashboardTab(DashboardSection.MONTHLY);
      }
    }
  }, [showDashboardTour, tourPhase, setTourPhase]);

  const openCustomizeDialogFromTour = () => {
    setOpenDialog(true);
    if (activeCustomizeGuide) {
      setCustomizeTourPhase('search');
    } else if (showOverviewTour) {
      syncPhase('customizeSearch');
    }
  };

  const advanceOverviewTour = () => {
    if (overviewTourPhase === 'monthlyTab') {
      setDashboardTab(DashboardSection.MONTHLY);
      syncPhase('month');
      return;
    }
    if (overviewTourPhase === 'month') {
      syncPhase('customize');
      return;
    }
    if (overviewTourPhase === 'customize') {
      setOpenDialog(true);
      syncPhase('customizeSearch');
      return;
    }
    if (overviewTourPhase === 'customizeSearch') {
      syncPhase('customizeMetrics');
      return;
    }
    if (overviewTourPhase === 'customizeMetrics') {
      syncPhase('customizeSave');
      return;
    }
    if (overviewTourPhase === 'customizeSave') {
      setOpenDialog(false);
      syncPhase('kpi');
      return;
    }
    if (overviewTourPhase === 'kpi') {
      setOpenKPIDialog(true);
      syncPhase('kpiForm');
      return;
    }
    if (overviewTourPhase === 'kpiForm') {
      syncPhase('kpiList');
      return;
    }
    if (overviewTourPhase === 'kpiList') {
      if (helpDashGuide === 'kpi') {
        finishDashboardGuide();
        return;
      }
      setOpenKPIDialog(false);
      setDashboardTab(DashboardSection.MONTHLY);
      syncPhase('esgCards');
      return;
    }
    if (overviewTourPhase === 'esgCards') {
      syncPhase('charts');
      return;
    }
    if (overviewTourPhase === 'charts') {
      setDashboardTab(DashboardSection.BRANCHES);
      syncPhase('branchesTab');
      return;
    }
    if (overviewTourPhase === 'branchesTab') {
      setDashboardTab(DashboardSection.BRANCHES);
      syncPhase('branches');
      return;
    }
    if (overviewTourPhase === 'branches') {
      syncPhase('compareBranches');
      return;
    }
    if (overviewTourPhase === 'compareBranches') {
      setOpenBranchCompare(true);
      syncPhase('compareDialog');
      return;
    }
    if (overviewTourPhase === 'compareDialog') {
      syncPhase('compareResults');
      return;
    }
    if (overviewTourPhase === 'compareResults') {
      if (helpDashGuide === 'branches') {
        finishDashboardGuide();
        return;
      }
      setOpenBranchCompare(false);
      setDashboardTab(DashboardSection.CARBON_AVOIDANCE);
      syncPhase('carbonTab');
      return;
    }
    if (overviewTourPhase === 'carbonTab') {
      setDashboardTab(DashboardSection.CARBON_AVOIDANCE);
      syncPhase('carbonDate');
      return;
    }
    if (overviewTourPhase === 'carbonDate') {
      syncPhase('carbonContent');
      return;
    }
    finishDashboardGuide();
  };

  const advanceDashboardGuide = () => {
    if (showOverviewTour && !activeCustomizeGuide) {
      advanceOverviewTour();
      return;
    }
    if (!activeCustomizeGuide) {
      finishDashboardGuide();
      return;
    }
    if (customizeTourPhase === 'button') {
      openCustomizeDialogFromTour();
      return;
    }
    if (customizeTourPhase === 'search') {
      setCustomizeTourPhase('metrics');
      return;
    }
    if (customizeTourPhase === 'metrics') {
      setCustomizeTourPhase('save');
      return;
    }
    finishDashboardGuide();
  };

  useEffect(() => {
    if (!activeCustomizeGuide) {
      setCustomizeTourPhase('button');
    }
  }, [activeCustomizeGuide]);

  useEffect(() => {
    if (
      (activeCustomizeGuide &&
        (customizeTourPhase === 'search' ||
          customizeTourPhase === 'metrics' ||
          customizeTourPhase === 'save')) ||
      overviewTourPhase === 'customizeSearch' ||
      overviewTourPhase === 'customizeMetrics' ||
      overviewTourPhase === 'customizeSave'
    ) {
      setOpenDialog(true);
    }
  }, [activeCustomizeGuide, customizeTourPhase, overviewTourPhase]);

  useEffect(() => {
    if (overviewTourPhase === 'kpiForm' || overviewTourPhase === 'kpiList') {
      setOpenKPIDialog(true);
    }
  }, [overviewTourPhase]);

  useEffect(() => {
    if (overviewTourPhase === 'compareDialog' || overviewTourPhase === 'compareResults') {
      setOpenBranchCompare(true);
    }
  }, [overviewTourPhase]);

  useEffect(() => {
    const helpGuide = new URLSearchParams(location.search).get('helpGuide');
    if (!helpGuide) return;

    // Leave language/appearance for Layout.
    if (helpGuide === HELP_GUIDE_CUSTOMIZE) {
      markHelpGuideActive(helpGuide);
      setHelpCustomizeGuide(true);
      setCustomizeTourPhase('button');
      navigate({ pathname: location.pathname, search: '' }, { replace: true });
      return;
    }

    const dashGuide = parseHelpDashboardGuide(helpGuide);
    if (!dashGuide) return;

    markHelpGuideActive(helpGuide);
    setHelpDashGuide(dashGuide);
    if (dashGuide === 'overview') {
      setOverviewTourPhase('monthlyTab');
      setDashboardTab(DashboardSection.MONTHLY);
    } else if (dashGuide === 'kpi') {
      setOverviewTourPhase('kpi');
      setDashboardTab(DashboardSection.MONTHLY);
    } else if (dashGuide === 'branches') {
      setOverviewTourPhase('branchesTab');
      setDashboardTab(DashboardSection.BRANCHES);
    } else {
      setOverviewTourPhase('carbonTab');
      setDashboardTab(DashboardSection.CARBON_AVOIDANCE);
    }

    navigate({ pathname: location.pathname, search: '' }, { replace: true });
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    if (!activeCustomizeGuide && !showOverviewTour) return;
    window.requestAnimationFrame(() => {
      const idMap: Partial<Record<OverviewTourPhase, string>> = {
        monthlyTab: 'self-setup-dashboard-tab-monthly',
        month: 'self-setup-dashboard-month',
        customize: 'self-setup-dashboard-customize',
        customizeSearch: 'self-setup-dashboard-search',
        customizeMetrics: 'self-setup-dashboard-metrics',
        customizeSave: 'self-setup-dashboard-save',
        kpi: 'self-setup-dashboard-kpi',
        kpiForm: 'self-setup-kpi-form',
        kpiList: 'self-setup-kpi-list',
        esgCards: 'self-setup-dashboard-esg',
        charts: 'self-setup-dashboard-charts',
        branchesTab: 'self-setup-dashboard-tab-branches',
        branches: 'self-setup-dashboard-branches',
        compareBranches: 'self-setup-dashboard-compare',
        compareDialog: 'self-setup-dashboard-compare-dialog',
        compareResults: 'self-setup-dashboard-compare-results',
        carbonTab: 'self-setup-dashboard-tab-carbon',
        carbonDate: 'self-setup-dashboard-carbon-date',
        carbonContent: 'self-setup-dashboard-carbon-content',
      };
      const id = showOverviewTour
        ? idMap[overviewTourPhase] ?? 'self-setup-dashboard-tabs'
        : customizeTourPhase === 'search'
          ? 'self-setup-dashboard-search'
          : customizeTourPhase === 'metrics'
            ? 'self-setup-dashboard-metrics'
            : customizeTourPhase === 'save'
              ? 'self-setup-dashboard-save'
              : 'self-setup-dashboard-customize';
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [
    activeCustomizeGuide,
    showOverviewTour,
    overviewTourPhase,
    customizeTourPhase,
  ]);
  const carbonToday = useMemo(() => startOfDay(new Date()), []);

  const selectedOverviewSet = useMemo(
    () => new Set(selectedOverviewWidgets),
    [selectedOverviewWidgets]
  );

  const selectedMonthDate = useMemo(() => parseDashboardMonth(selectedMonth), [selectedMonth]);
  const monthLabel = useMemo(
    () =>
      new Intl.DateTimeFormat(numberLocale(i18n.language), { month: 'long', year: 'numeric' }).format(
        selectedMonthDate,
      ),
    [i18n.language, selectedMonthDate],
  );

  const carbonPeriodValid = isCarbonReportPeriodValid(carbonPeriod);
  const carbonPeriodLabel = useMemo(() => {
    // Localized range for display (PDF exports keep `formatReportPeriodLabel`).
    const formatter = new Intl.DateTimeFormat(numberLocale(i18n.language), {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }) as Intl.DateTimeFormat & { formatRange?: (start: Date, end: Date) => string };
    try {
      if (formatter.formatRange) return formatter.formatRange(carbonPeriod.startDate, carbonPeriod.endDate);
    } catch {
      /* fall back below */
    }
    return formatReportPeriodLabel(
      format(carbonPeriod.startDate, 'yyyy-MM-dd'),
      format(carbonPeriod.endDate, 'yyyy-MM-dd'),
    );
  }, [carbonPeriod, i18n.language]);

  const operationalDashboard = esgMonthly
    ? (esgMonthly as unknown as DashboardStatistics)
    : null;

  const esgMonthlyChartMetrics = useMemo(
    () => selectedEsgMonthlyMetrics.filter((m) => isEsgMonthlyChartMetric(m)),
    [selectedEsgMonthlyMetrics]
  );

  const operationalChartStats = useMemo(() => {
    if (!operationalDashboard) return [] as StatisticType[];
    return selectedStats.filter((s) => {
      if (!operationalStatsForOrg.includes(s) || !isOperationalChartStat(s)) return false;
      const v = operationalDashboard[s as keyof DashboardStatistics];
      return Array.isArray(v);
    });
  }, [selectedStats, operationalDashboard, operationalStatsForOrg]);

  const hasMonthlyCharts =
    esgMonthlyChartMetrics.length > 0 || operationalChartStats.length > 0;

  const hasNoOperationalData = (() => {
    if (!esgMonthly) return true;
    const stripped: Record<string, unknown> = { ...esgMonthly };
    ESG_MONTHLY_NON_OPERATIONAL_KEYS.forEach((k) => {
      delete stripped[k];
    });
    return Object.values(stripped).every(
      (value) =>
        value === 0 ||
        value === null ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0)
    );
  })();

  const orderedMonthlyNumberCards = useMemo(() => {
    const selected = new Set(selectedEsgMonthlyMetrics);
    const row: Array<
      | { kind: 'esg'; metric: EsgMonthlyMetricType }
      | { kind: 'ops'; stat: StatisticType }
    > = [];

    for (const m of ESG_MONTHLY_SUMMARY_METRIC_ORDER) {
      if (selected.has(m) && !isEsgMonthlyChartMetric(m)) {
        row.push({ kind: 'esg', metric: m });
      }
    }

    const showAvgPrice =
      !hasNoOperationalData &&
      selectedStats.includes(StatisticType.AVERAGE_INCENTIVE_PER_KG) &&
      operationalStatsForOrg.includes(StatisticType.AVERAGE_INCENTIVE_PER_KG) &&
      operationalDashboard &&
      typeof operationalDashboard.average_incentive_per_kg === 'number';
    if (showAvgPrice) {
      row.push({ kind: 'ops', stat: StatisticType.AVERAGE_INCENTIVE_PER_KG });
    }

    for (const m of ESG_MONTHLY_UTILITY_METRIC_ORDER) {
      if (selected.has(m) && !isEsgMonthlyChartMetric(m)) {
        row.push({ kind: 'esg', metric: m });
      }
    }

    for (const stat of operationalStatsForOrg) {
      if (!selectedStats.includes(stat) || isOperationalChartStat(stat)) continue;
      if (stat === StatisticType.AVERAGE_INCENTIVE_PER_KG) continue;
      row.push({ kind: 'ops', stat });
    }

    return row;
  }, [
    selectedEsgMonthlyMetrics,
    selectedStats,
    operationalDashboard,
    operationalStatsForOrg,
    hasNoOperationalData,
  ]);

  const esgBranchRows = esgByBranch
    ? [
        ...esgByBranch.branches,
        ...(esgByBranch.unassigned_waste ? [esgByBranch.unassigned_waste] : []),
      ]
    : [];
  const allBranchesExpanded =
    esgBranchRows.length > 0 && esgBranchRows.every((row) => expandedBranches.has(row.collection_site_id));

  const getDisplayTitle = (statKey: string): string => getStatisticLabel(t, statKey);

  const getEsgMetricTitle = (metric: EsgMonthlyMetricType): string =>
    getEsgMonthlyMetricLabel(t, metric);

  const customizeDialogCopy =
    dashboardTab === DashboardSection.MONTHLY
      ? { title: t('dashboard.customizeMonthly'), hint: t('dashboard.customizeMonthlyHint') }
      : dashboardTab === DashboardSection.OVERVIEW
        ? { title: t('dashboard.customizeOverview'), hint: t('dashboard.customizeOverviewHint') }
        : dashboardTab === DashboardSection.SCOPES
          ? { title: t('dashboard.customizeScopes'), hint: t('dashboard.customizeScopesHint') }
          : dashboardTab === DashboardSection.BRANCHES
            ? { title: t('dashboard.customizeBranches'), hint: t('dashboard.customizeBranchesHint') }
            : { title: t('dashboard.customizeCarbon'), hint: t('dashboard.customizeCarbonHint') };

  const monthlyCustomizeItems = [
    ...ESG_MONTHLY_EXTRA_WIDGETS.map((id) => ({
      id,
      label: t(`dashboard.monthlyExtras.${id}`),
    })),
    ...PICKABLE_ESG_MONTHLY_METRICS.map((id) => ({
      id,
      label: getEsgMetricTitle(id),
    })),
    ...operationalStatsForOrg.map((id) => ({
      id,
      label: getDisplayTitle(id),
    })),
  ];
  const monthlyCustomizeSelected = [
    ...selectedMonthlyExtras,
    ...selectedEsgMonthlyMetrics,
    ...selectedStats.filter((s) => operationalStatsForOrg.includes(s)),
  ];

  const scopesCustomizeItems = ESG_SCOPES_WIDGETS.map((id) => ({
    id,
    label: t(`dashboard.scopeWidgets.${id}`),
  }));
  const branchesCustomizeItems = ESG_BRANCHES_WIDGETS.map((id) => ({
    id,
    label: t(`dashboard.branchWidgets.${id}`),
  }));
  const carbonCustomizeItems = ESG_CARBON_WIDGETS.map((id) => ({
    id,
    label: t(`dashboard.carbonWidgets.${id}`),
  }));

  useEffect(() => {
    loadDashboardPreferences();
    
    // Get selected month from sessionStorage or use current month
    const storedMonth = sessionStorage.getItem('selectedMonth');
    const currentDate = new Date();
    const formattedDate = format(currentDate, "yyyy-MM-dd'T'HH:mm:ss'Z'");
    
    const monthToUse = storedMonth || formattedDate;
    setSelectedMonth(monthToUse);
    sessionStorage.setItem('selectedMonth', monthToUse);
    
    // Load operational picks for the current organization type.
    if (orgType) {
      const next = loadOperationalStatsForOrg(orgType);
      setSelectedStats(next);
      persistOperationalStatsForOrg(orgType, next);
    } else {
      setSelectedStats([]);
    }

    const storedEsgMetrics = sessionStorage.getItem('esgMonthlySelectedMetrics');
    if (storedEsgMetrics) {
      try {
        const parsed = JSON.parse(storedEsgMetrics) as string[];
        const mapped = parsed.map((m) => LEGACY_ESG_MONTHLY_METRIC_KEY[m] ?? m);
        const migrated = sanitizePickableIds(
          mapped,
          PICKABLE_ESG_MONTHLY_METRICS,
          [
            EsgMonthlyMetricType.WATER_USE,
            EsgMonthlyMetricType.ELECTRICITY_USE,
            EsgMonthlyMetricType.OIL_USE,
            EsgMonthlyMetricType.GAS_USE,
            EsgMonthlyMetricType.HEALTH_SAFETY,
          ],
        );
        setSelectedEsgMonthlyMetrics(migrated);
        sessionStorage.setItem('esgMonthlySelectedMetrics', JSON.stringify(migrated));
      } catch {
        /* ignore invalid */
      }
    }

    const storedMonthlyExtras = sessionStorage.getItem('esgMonthlySelectedExtras');
    if (storedMonthlyExtras) {
      try {
        const parsed = JSON.parse(storedMonthlyExtras) as unknown;
        const next = sanitizePickableIds(
          expandLegacyScopeEmissions(parsed),
          ESG_MONTHLY_EXTRA_WIDGETS,
        );
        setSelectedMonthlyExtras(next);
        sessionStorage.setItem('esgMonthlySelectedExtras', JSON.stringify(next));
      } catch {
        /* ignore invalid */
      }
    }

    const storedOverviewWidgets = sessionStorage.getItem('esgOverviewSelectedWidgets');
    if (storedOverviewWidgets) {
      try {
        const parsed = JSON.parse(storedOverviewWidgets) as string[];
        const next = sanitizePickableIds(
          expandLegacyScopeEmissions(parsed),
          PICKABLE_ESG_OVERVIEW_WIDGETS,
          [
            'scope_1',
            'scope_2',
            'utility_water',
            'utility_electricity',
            'utility_oil',
            'utility_gas',
            'summary_health_safety',
          ],
        );
        setSelectedOverviewWidgets(next);
        sessionStorage.setItem('esgOverviewSelectedWidgets', JSON.stringify(next));
      } catch {
        /* ignore invalid */
      }
    }

    const storedScopes = sessionStorage.getItem('esgScopesSelectedWidgets');
    if (storedScopes) {
      try {
        setSelectedScopesWidgets(
          sanitizePickableIds(JSON.parse(storedScopes), ESG_SCOPES_WIDGETS),
        );
      } catch {
        /* ignore invalid */
      }
    }

    const storedBranches = sessionStorage.getItem('esgBranchesSelectedWidgets');
    if (storedBranches) {
      try {
        setSelectedBranchesWidgets(
          sanitizePickableIds(JSON.parse(storedBranches), ESG_BRANCHES_WIDGETS, ["health_safety"]),
        );
      } catch {
        /* ignore invalid */
      }
    }

    const storedCarbon = sessionStorage.getItem('esgCarbonSelectedWidgets');
    if (storedCarbon) {
      try {
        setSelectedCarbonWidgets(
          sanitizePickableIds(JSON.parse(storedCarbon), ESG_CARBON_WIDGETS),
        );
      } catch {
        /* ignore invalid */
      }
    }

    if (selectedOrganization?.id && selectedOrganization?.organization_type) {
      fetchEsgDashboard(
        selectedOrganization.id,
        selectedOrganization.organization_type,
        monthToUse
      );
    }
  }, [selectedOrganization?.id, selectedOrganization?.organization_type, fetchEsgDashboard]);

  useEffect(() => {
    if (!selectedOrganization?.id || !carbonPeriodValid) {
      resetCarbonReport();
      return;
    }
    fetchCarbonAvoidanceReport(
      selectedOrganization.id,
      carbonReportPeriodToParams(carbonPeriod)
    );
  }, [
    selectedOrganization?.id,
    carbonPeriod,
    carbonPeriodValid,
    fetchCarbonAvoidanceReport,
    resetCarbonReport,
  ]);

  const updateCarbonPeriod = (patch: Partial<CarbonReportPeriod>) => {
    setCarbonPeriod((prev) => {
      const next = { ...prev, ...patch };
      writeCarbonReportPeriodToSession(next);
      return next;
    });
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, statId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedStat(statId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedStat(null);
  };

  const handleChartTypeChange = (type: 'bar' | 'line' | 'area' | 'pie') => {
    if (selectedStat) {
      setChartTypes(prev => ({ ...prev, [selectedStat]: type }));
      handleMenuClose();
    }
  };

  const handleOpenDialog = () => {
    setSearchQuery('');
    setOpenDialog(true);
    if (activeCustomizeGuide) {
      setCustomizeTourPhase('search');
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    if (
      activeCustomizeGuide &&
      (customizeTourPhase === 'search' ||
        customizeTourPhase === 'metrics' ||
        customizeTourPhase === 'save')
    ) {
      // Keep tour active on the button if they close without finishing
      setCustomizeTourPhase('button');
    }
  };

  const handleSavePreferences = () => {
    if (dashboardTab === DashboardSection.MONTHLY) {
      const ops = selectedStats.filter((s) => operationalStatsForOrg.includes(s));
      setSelectedStats(ops);
      if (orgType) persistOperationalStatsForOrg(orgType, ops);
      sessionStorage.setItem('esgMonthlySelectedMetrics', JSON.stringify(selectedEsgMonthlyMetrics));
      sessionStorage.setItem('esgMonthlySelectedExtras', JSON.stringify(selectedMonthlyExtras));
    } else if (dashboardTab === DashboardSection.OVERVIEW) {
      sessionStorage.setItem('esgOverviewSelectedWidgets', JSON.stringify(selectedOverviewWidgets));
    } else if (dashboardTab === DashboardSection.SCOPES) {
      sessionStorage.setItem('esgScopesSelectedWidgets', JSON.stringify(selectedScopesWidgets));
    } else if (dashboardTab === DashboardSection.BRANCHES) {
      sessionStorage.setItem('esgBranchesSelectedWidgets', JSON.stringify(selectedBranchesWidgets));
    } else if (dashboardTab === DashboardSection.CARBON_AVOIDANCE) {
      sessionStorage.setItem('esgCarbonSelectedWidgets', JSON.stringify(selectedCarbonWidgets));
    }
    if (activeCustomizeGuide) {
      finishDashboardGuide();
    } else {
      handleCloseDialog();
    }
  };

  const handleOpenKPIDialog = () => {
    setOpenKPIDialog(true);
  };

  const handleCloseKPIDialog = () => {
    setOpenKPIDialog(false);
  };

  const handleMonthChange = (date: string) => {
    setSelectedMonth(date);
    sessionStorage.setItem('selectedMonth', date);
    if (selectedOrganization?.id && selectedOrganization?.organization_type) {
      fetchEsgDashboard(
        selectedOrganization.id,
        selectedOrganization.organization_type,
        date
      );
    }
  };

  const KPI_UNITS: Record<string, string> = {
    // orders
    total_orders: "orders",
    pending_orders: "orders",
    completed_orders: "orders",
    cancelled_orders: "orders",

    // revenue & incentives
    total_revenue: "SAR",
    average_incentive_per_kg: "SAR/kg",

    // percentages
    full_percentage: "%",
    completion_rate: "%",
    cancellation_rate: "%",
    waste_diversion_rate: "%",
    quality_compliance_rate: "%",
    rejected_waste_percentage: "%",
    storage_capacity_utilization: "%",
    processing_capacity_utilization: "%",

    // weights
    total_waste_amount: "kg",
    completed_waste_amount: "kg",
    sustainable_supply: "kg",

    // time
    average_processing_time_hours: "hours",
  };

  const getUnitForStat = (stat: StatisticType): string => {
    switch (stat) {
      case StatisticType.TIME_FROM_FULL_TO_COLLECTION:
      case StatisticType.TIME_FROM_COLLECTION_TO_DELIVERY:
      case StatisticType.AVERAGE_PROCESSING_TIME:
        return 'hours';
      case StatisticType.FULL_PERCENTAGE:
      case StatisticType.WASTE_DIVERSION_RATE:
      case StatisticType.QUALITY_COMPLIANCE_RATE:
      case StatisticType.REJECTED_WASTE_PERCENTAGE:
      case StatisticType.PROCESSING_CAPACITY_UTILIZATION:
      case StatisticType.MAINTENANCE_SCHEDULE_COMPLIANCE:
      case StatisticType.DRIVER_PERFORMANCE_SCORE:
      case StatisticType.CUSTOMER_SATISFACTION_SCORE:
      case StatisticType.WASTE_COMPACTION_EFFICIENCY:
      case StatisticType.STORAGE_CAPACITY_UTILIZATION:
      case StatisticType.WASTE_QUALITY_RATING:
      case StatisticType.AVERAGE_CONTAINER_FILL_RATE:
        return '%';
      case StatisticType.RECYCLER_APPETITE:
      case StatisticType.SUSTAINABLE_SUPPLY:
      case StatisticType.FUEL_CONSUMPTION_PER_TRIP:
        return 'kg/day';
      case StatisticType.AVERAGE_INCENTIVE_PER_KG:
        return 'SAR/kg';
      case StatisticType.TOTAL_REVENUE:
      case StatisticType.TOTAL_REVENUE_LAST_MONTH:
        return 'SAR';
      case StatisticType.COLLECTION_FREQUENCY:
        return 'times/day';
      case StatisticType.AVERAGE_ROUTE_EFFICIENCY:
        return 'km/L';
      case StatisticType.TRIPS_TODAY_COUNT:
      case StatisticType.EMERGENCY_COLLECTION_REQUESTS:
        return 'trips';
      case StatisticType.TRUCK_DRIVER_COUNT:
        return 'drivers';
      default:
        return '';
    }
  };


  /** Operational units come from `getUnitForStat` as English codes; localize for display. */
  const OPS_UNIT_KEYS: Record<string, string> = {
    hours: 'units.hours',
    '%': 'units.percent',
    kg: 'units.kg',
    'kg/day': 'units.kgPerDay',
    'SAR/kg': 'units.sarPerKg',
    SAR: 'units.sar',
    'times/day': 'units.timesPerDay',
    'km/L': 'units.kmPerL',
    trips: 'units.trips',
    drivers: 'units.drivers',
    orders: 'units.orders',
  };
  const localizeOpsUnit = (unit: string) => (OPS_UNIT_KEYS[unit] ? t(OPS_UNIT_KEYS[unit]) : unit);
  const opsValueKind = (unit: string): MetricValueKind =>
    unit === '%'
      ? 'percent'
      : unit === 'hours'
        ? 'hours'
        : unit === 'SAR' || unit === 'SAR/kg'
          ? 'sar'
          : 'decimal';

  const renderStatCard = (
    stat: StatisticType,
    operational: DashboardStatistics | null | undefined,
    framed = true,
  ) => {
    if (!operational) return null;
    const key = stat as keyof DashboardStatistics;
    if (operational[key] === undefined || operational[key] === null) return null;

    const data = operational[key] as unknown;
    const chartType = chartTypes[stat] || 'bar';

    if (Array.isArray(data)) {
      if (stat === StatisticType.SUPPLY_PER_RECYCLER) {
        return (
          <ChartCard
            title={getDisplayTitle(stat)}
            icon={ESG_ICONS.operations}
            data={data}
            type={chartType}
            dataKey="supply_amount"
            nameKey="recycler_name"
            valueKind="mass_kg"
            unit={t('units.kg')}
            seriesLabel={getDisplayTitle(stat)}
            onMenuClick={(e) => handleMenuOpen(e, stat)}
          />
        );
      }
      if (stat === StatisticType.INCOMING_WASTE_BREAKDOWN) {
        return (
          <ChartCard
            title={getDisplayTitle(stat)}
            icon={ESG_ICONS.timeSeries}
            data={data}
            type={chartType}
            dataKey="amount"
            nameKey="date"
            seriesKey="waste_type"
            valueKind="mass_kg"
            unit={t('units.kg')}
            onMenuClick={(e) => handleMenuOpen(e, stat)}
          />
        );
      }
      if (stat === StatisticType.WASTE_TYPE_BREAKDOWN) {
        const transformedData = (data as Array<{ waste_type: string; total_kg: number }>).map((item) => ({
          waste_type: item.waste_type,
          total_kg: item.total_kg,
        }));
        return (
          <ChartCard
            title={getDisplayTitle(stat)}
            icon={ESG_ICONS.materialFlows}
            data={transformedData}
            type="pie"
            dataKey="total_kg"
            nameKey="waste_type"
            valueKind="mass_kg"
            unit={t('units.kg')}
            onMenuClick={(e) => handleMenuOpen(e, stat)}
          />
        );
      }
    }

    if (typeof data === 'number') {
      const currentValue = data;
      const previousKey = `previous_month_${stat}` as keyof DashboardStatistics;
      const rawPrev = operational[previousKey];
      const previousValue = typeof rawPrev === 'number' ? rawPrev : undefined;
      const change =
        previousValue === undefined || previousValue === 0
          ? 0
          : ((currentValue - previousValue) / previousValue) * 100;
      const rawUnit = getUnitForStat(stat);

      return (
        <StatCard
          title={getDisplayTitle(stat)}
          value={currentValue}
          unit={localizeOpsUnit(rawUnit)}
          valueKind={opsValueKind(rawUnit)}
          polarity={operationalStatPolarity(stat)}
          metricKey={stat}
          trend={change}
          frameless={!framed}
          hideMenu
          onMenuClick={(e) => handleMenuOpen(e, stat)}
          icon={stat === StatisticType.AVERAGE_INCENTIVE_PER_KG ? ESG_ICONS.cost : undefined}
        />
      );
    }

    return null;
  };

  type EsgSource = EsgDashboardMonthlyResponse | EsgDashboardOverviewResponse;
  const comparisonLabel = t('dashboard.vsLastMonth');

  /** Month-over-month delta: relative % for quantities, percentage points for the rate. */
  const esgDelta = (
    metric: EsgMonthlyMetricType,
    esg: EsgDashboardMonthlyResponse,
  ): KpiFigureProps['delta'] => {
    if (metric === EsgMonthlyMetricType.WASTE_DIVERSION_RATE) {
      const prev = esg.previous_month_waste_diversion_rate;
      if (prev === null || prev === undefined) return undefined;
      const points = (esg.waste_diversion_rate_percent ?? 0) - prev;
      if (!points) return undefined;
      return { value: points, kind: 'points', polarity: ESG_METRIC_POLARITY[metric], comparisonLabel };
    }
    const mom = esgMonthlyMetricMoM(esg, metric);
    if (mom.hideTrend || !mom.trend) return undefined;
    return { value: mom.trend, kind: 'relative', polarity: ESG_METRIC_POLARITY[metric], comparisonLabel };
  };

  const utilityFigure = (
    label: string,
    icon: KpiFigureProps['icon'],
    kind: MetricValueKind,
    unit: string,
    consumption: number | null | undefined,
    costSar: number | null | undefined,
  ): KpiFigureProps => {
    const f = fmt.metric(kind, consumption ?? 0);
    return {
      label,
      icon,
      value: f.text,
      unit,
      secondary: t('dashboard.context.cost', { value: fmt.withUnit('sar', costSar ?? 0) }),
    };
  };

  /** Editorial figure for an ESG metric (Monthly and Overview share the payload shape). */
  const esgFigure = (
    metric: EsgMonthlyMetricType,
    source: EsgSource,
    { delta, context }: { delta?: KpiFigureProps['delta']; context?: React.ReactNode } = {},
  ): KpiFigureProps | null => {
    const label = getEsgMetricTitle(metric);
    switch (metric) {
      case EsgMonthlyMetricType.TOTAL_WASTE_DIVERTED: {
        const kg = source.total_waste_diverted_kg ?? 0;
        const f = fmt.metric('mass_kg', kg, { scaleMass: true });
        return {
          label,
          icon: ESG_ICONS.wasteCollected,
          value: f.text,
          unit: f.unit,
          context,
          delta,
          info: Math.abs(kg) >= 1000 ? fmt.withUnit('mass_kg', kg) : undefined,
        };
      }
      case EsgMonthlyMetricType.WASTE_DIVERSION_RATE: {
        const f = fmt.metric('percent', source.waste_diversion_rate_percent ?? 0);
        return {
          label,
          icon: ESG_ICONS.completionRate,
          value: f.text,
          unit: f.unit,
          context,
          delta,
        };
      }
      case EsgMonthlyMetricType.TOTAL_CO2: {
        const f = fmt.metric('tco2e', pickTotalAvoidedTco2e(source));
        return {
          label,
          icon: ESG_ICONS.avoidedEmissions,
          value: f.text,
          unit: f.unit,
          context,
          delta,
          info: uncountedPickupsNote(source, t) ?? undefined,
        };
      }
      case EsgMonthlyMetricType.FILL_LEVEL_REPORT_COUNT:
        return {
          label,
          icon: ESG_ICONS.fillReports,
          value: fmt.metric('count', source.fill_level_report_count).text,
          unit: t('units.reports'),
          context,
          delta,
        };
      case EsgMonthlyMetricType.TRAINING_HOURS_TOTAL:
        if (source.training_hours_total === null) {
          return {
            label,
            icon: ESG_ICONS.training,
            value: '—',
            context: t('dashboard.alerts.trainingNotAvailable'),
          };
        }
        return {
          label,
          icon: ESG_ICONS.training,
          value: fmt.metric('hours', source.training_hours_total).text,
          unit: t('units.hours'),
          context: context ?? t('dashboard.context.organizationWide'),
          delta,
        };
      case EsgMonthlyMetricType.WATER_USE:
        return {
          ...utilityFigure(label, ESG_ICONS.water, 'm3', t('units.units'), source.water_consumption, source.water_amount_sar),
          delta,
        };
      case EsgMonthlyMetricType.ELECTRICITY_USE:
        return {
          ...utilityFigure(
            label,
            ESG_ICONS.electricity,
            'kwh',
            t('units.kwh'),
            source.electricity_consumption_kwh,
            source.electricity_amount_sar,
          ),
          delta,
        };
      case EsgMonthlyMetricType.OIL_USE:
        return {
          ...utilityFigure(label, ESG_ICONS.oil, 'litres', t('units.liters'), source.oil_consumption_liters, source.oil_amount_sar),
          delta,
        };
      case EsgMonthlyMetricType.GAS_USE:
        return {
          ...utilityFigure(label, ESG_ICONS.gas, 'm3', t('units.m3'), source.gas_consumption_m3, source.gas_amount_sar),
          delta,
        };
      default:
        return null;
    }
  };

  const periodChip = (text: React.ReactNode) => (
    <Chip
      size="small"
      variant="outlined"
      icon={<ESG_ICONS.period sx={{ fontSize: 14 }} />}
      label={text}
      sx={{ color: 'text.secondary' }}
    />
  );

  const customizeAction = (
    <Button
      size="small"
      variant="outlined"
      color="inherit"
      startIcon={<ESG_ICONS.customize fontSize="small" />}
      onClick={() => handleOpenDialog()}
    >
      {t('dashboard.customize')}
    </Button>
  );

  const figureCell = (props: KpiFigureProps | null, key: string) =>
    props ? <KpiFigure key={key} {...props} /> : null;

  interface EsgTabSelection {
    performance: EsgMonthlyMetricType[];
    avoidedHeadline: boolean;
    avoidedStream: boolean;
    inventoryScope1: boolean;
    inventoryScope2: boolean;
    resources: EsgMonthlyMetricType[];
    hse: boolean;
    social: EsgMonthlyMetricType[];
    operations: StatisticType[];
    flowsRecycled: boolean;
    flowsCharts: StatisticType[];
    chartKeys: { recycled: string; stream: string };
  }

  /**
   * Monthly and Overview share one editorial structure:
   * performance → avoided emissions (outside inventory) → GHG inventory →
   * resource use → social & operations → material flows. Methodology lives in
   * the Knowledge Base ("Methodology & data quality").
   */
  const renderEsgTab = (source: EsgSource, sel: EsgTabSelection, mode: 'monthly' | 'overview') => {
    const isMonthly = mode === 'monthly';
    const periodText = isMonthly ? monthLabel : t('dashboard.allTime');
    const fig = (metric: EsgMonthlyMetricType, context?: React.ReactNode) =>
      esgFigure(metric, source, {
        delta: isMonthly ? esgDelta(metric, source as EsgDashboardMonthlyResponse) : undefined,
        context,
      });

    const lead = sel.performance.includes(EsgMonthlyMetricType.TOTAL_WASTE_DIVERTED)
      ? fig(EsgMonthlyMetricType.TOTAL_WASTE_DIVERTED, t('dashboard.context.completedPickups', { period: periodText }))
      : null;
    const side = sel.performance.includes(EsgMonthlyMetricType.WASTE_DIVERSION_RATE)
      ? fig(EsgMonthlyMetricType.WASTE_DIVERSION_RATE, periodText)
      : null;

    const showAvoided = sel.avoidedHeadline || sel.avoidedStream;
    const avoidedFigure = sel.avoidedHeadline
      ? fig(EsgMonthlyMetricType.TOTAL_CO2, t('dashboard.context.byDeliveryDate', { period: periodText }))
      : null;
    // Modelled (baseline) energy from the same calculation runs: shown with avoided emissions,
    // never with resource use (which is measured consumption).
    const energy = fmt.metric('kwh', source.total_energy_saved_kwh ?? 0);
    const energyFigure: KpiFigureProps | null = avoidedFigure
      ? {
          label: t('carbonAvoidance.energySaved'),
          icon: ESG_ICONS.energySavings,
          value: energy.text,
          unit: energy.unit,
          context: t('dashboard.context.byDeliveryDate', { period: periodText }),
        }
      : null;
    const resourceCells = sel.resources.map((m) => figureCell(fig(m), m)).filter(Boolean);
    const socialCells = sel.social.map((m) => figureCell(fig(m), m)).filter(Boolean);
    const opsSource = source as unknown as DashboardStatistics;
    const opsCells = sel.operations
      .map((stat) => {
        const node = renderStatCard(stat, opsSource, false);
        return node ? <React.Fragment key={stat}>{node}</React.Fragment> : null;
      })
      .filter(Boolean);

    const flowCharts: React.ReactNode[] = [];
    if (sel.flowsRecycled) {
      const rows = source.waste_type_material_breakdown ?? [];
      flowCharts.push(
        rows.length ? (
          <EsgRecycledWasteByTypeChart
            key="recycled"
            rows={rows}
            title={t('metrics.waste_type_material')}
            caption={periodText}
            chartType={chartTypes[sel.chartKeys.recycled] || 'pie'}
            onMenuClick={(e) => handleMenuOpen(e, sel.chartKeys.recycled)}
          />
        ) : (
          <EmptyState
            key="recycled-empty"
            icon={ESG_ICONS.materialFlows}
            title={t('metrics.waste_type_material')}
            description={t('dashboard.alerts.noWasteBreakdown')}
            minHeight={240}
          />
        ),
      );
    }
    sel.flowsCharts.forEach((stat) => {
      const node = renderStatCard(stat, opsSource);
      if (node) flowCharts.push(<React.Fragment key={stat}>{node}</React.Fragment>);
    });

    const twoUp = (count: number) => ({
      display: 'grid',
      gap: { xs: 2, md: 2.5 },
      gridTemplateColumns: {
        xs: 'minmax(0, 1fr)',
        md: count > 1 ? 'minmax(0, 7fr) minmax(0, 5fr)' : 'minmax(0, 1fr)',
      },
      alignItems: 'stretch',
    });

    const coreSections = (
      <>
        {(lead || side) && (
          <EsgSection
            title={t('dashboard.sections.environmentalPerformance')}
            icon={ESG_ICONS.wasteCollected}
            description={t('dashboard.sectionDescriptions.environmentalPerformance')}
            meta={periodChip(periodText)}
          >
            <PerformanceHero lead={lead} side={side} />
          </EsgSection>
        )}

        {showAvoided && (
          <EsgSection
            title={t('dashboard.sections.avoidedEmissions')}
            icon={ESG_ICONS.avoidedEmissions}
            description={t('dashboard.sectionDescriptions.avoidedEmissions')}
          >
            <Box sx={{ display: 'grid', gap: { xs: 2, md: 2.5 } }}>
              {(avoidedFigure || sel.avoidedStream) && (
                <Box
                  sx={{
                    display: 'grid',
                    gap: { xs: 2, md: 2.5 },
                    alignItems: 'stretch',
                    gridTemplateColumns: {
                      xs: 'minmax(0, 1fr)',
                      md: avoidedFigure && sel.avoidedStream ? 'minmax(0, 5fr) minmax(0, 7fr)' : 'minmax(0, 1fr)',
                    },
                  }}
                >
                  {avoidedFigure && (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: { xs: 2.5, md: 3 },
                        borderRadius: `${ESG_CONTENT_SHAPE.md}px`,
                        bgcolor: 'background.paper',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        gap: 2,
                      }}
                    >
                      <Box sx={{ display: 'grid', gap: 2.5 }}>
                        <KpiFigure {...avoidedFigure} size="hero" />
                        {energyFigure ? (
                          <Box sx={(th) => ({ pt: 2, borderTop: `1px solid ${th.palette.divider}` })}>
                            <KpiFigure {...energyFigure} size="md" />
                          </Box>
                        ) : null}
                      </Box>
                    </Paper>
                  )}
                  {sel.avoidedStream && (
                    <CarbonAvoidedByStreamChart
                      streams={source.avoided_tco2e_by_stream ?? []}
                      title={t('metrics.carbon_avoidance_stream')}
                      caption={t('units.tco2e')}
                      chartType={chartTypes[sel.chartKeys.stream] || 'pie'}
                      onMenuClick={(e) => handleMenuOpen(e, sel.chartKeys.stream)}
                    />
                  )}
                </Box>
              )}
            </Box>
          </EsgSection>
        )}

        {(sel.inventoryScope1 || sel.inventoryScope2) && source.scope_emissions && (
          <EsgSection
            title={t('dashboard.sections.ghgInventory')}
            icon={ESG_ICONS.ghgInventory}
            description={t('dashboard.sectionDescriptions.ghgInventory')}
          >
            <ScopeEmissionsPanel
              summary={source.scope_emissions}
              showBreakdown={false}
              showScope1={sel.inventoryScope1}
              showScope2={sel.inventoryScope2}
              context={periodText}
            />
          </EsgSection>
        )}

        {resourceCells.length > 0 && (
          <EsgSection
            title={t('dashboard.sections.resourceUse')}
            icon={ESG_ICONS.electricity}
            description={t('dashboard.sectionDescriptions.resourceUse')}
          >
            <MetricStrip columns={{ xs: 2, sm: 2, md: Math.min(resourceCells.length, 4) }}>{resourceCells}</MetricStrip>
          </EsgSection>
        )}

        {sel.hse && (
          <HseDashboardSection
            hse={source.hse}
            previous={isMonthly ? (source as EsgDashboardMonthlyResponse).previous_month_hse : undefined}
            periodText={periodText}
            comparisonLabel={comparisonLabel}
          />
        )}

        {(socialCells.length > 0 || opsCells.length > 0 || (isMonthly && hasNoOperationalData)) && (
          <EsgSection
            title={t('dashboard.sections.socialOperations')}
            icon={ESG_ICONS.social}
            description={t('dashboard.sectionDescriptions.socialOperations')}
          >
            <Box sx={{ display: 'grid', gap: { xs: 2, md: 2.5 } }}>
              {socialCells.length > 0 && (
                <MetricStrip columns={{ xs: 2, sm: 2, md: Math.min(socialCells.length, 4) }}>{socialCells}</MetricStrip>
              )}
              {opsCells.length > 0 && (
                <MetricStrip columns={{ xs: 2, sm: 2, md: 3, lg: 4 }}>{opsCells}</MetricStrip>
              )}
              {isMonthly && hasNoOperationalData && (
                <Alert severity="info">{t('dashboard.alerts.noOperationalDataMonthly')}</Alert>
              )}
            </Box>
          </EsgSection>
        )}
      </>
    );

    const flowsSection = (
      <EsgSection
        title={t('dashboard.sections.materialFlows')}
        icon={ESG_ICONS.materialFlows}
        description={t('dashboard.sectionDescriptions.materialFlows')}
      >
        {flowCharts.length > 0 ? (
          <Box sx={twoUp(flowCharts.length)}>{flowCharts}</Box>
        ) : (
          <EmptyState
            icon={ESG_ICONS.chart}
            title={t('dashboard.alerts.noCharts')}
            action={customizeAction}
            minHeight={200}
          />
        )}
      </EsgSection>
    );

    return (
      <>
        {isMonthly ? (
          <SetupCoachmark
            open={showEsgCardsTip}
            title={t('selfSetup.tour.dashboardEsgTitle')}
            subtitle={t('selfSetup.tour.dashboardEsgSubtitle')}
            onDismiss={softDismissDashboardTour}
            onNext={advanceDashboardGuide}
            nextLabel={t('selfSetup.tourNext')}
            fullWidth
          >
            <Box id="self-setup-dashboard-esg">{coreSections}</Box>
          </SetupCoachmark>
        ) : (
          coreSections
        )}

        {isMonthly ? (
          <SetupCoachmark
            open={showChartsTip}
            title={t('selfSetup.tour.dashboardChartsTitle')}
            subtitle={t('selfSetup.tour.dashboardChartsSubtitle')}
            onDismiss={softDismissDashboardTour}
            onNext={advanceDashboardGuide}
            nextLabel={t('selfSetup.tourNext')}
            fullWidth
          >
            <Box id="self-setup-dashboard-charts">{flowsSection}</Box>
          </SetupCoachmark>
        ) : (
          (sel.flowsRecycled || sel.flowsCharts.length > 0) && flowsSection
        )}

      </>
    );
  };

  if (!selectedOrganization?.id) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px',
        }}
      >
        <Typography>{t('dashboard.selectOrganization')}</Typography>
      </Box>
    );
  }

  const formatSar = (amount: number) => fmt.withUnit('sar', amount);

  const skeletonVariant =
    dashboardTab === DashboardSection.MONTHLY ||
    dashboardTab === DashboardSection.SCOPES
      ? 'monthly'
      : dashboardTab === DashboardSection.OVERVIEW
        ? 'overview'
        : dashboardTab === DashboardSection.BRANCHES
          ? 'branches'
          : 'carbon';

  const isTabLoading =
    (esgLoading && dashboardTab !== DashboardSection.CARBON_AVOIDANCE) ||
    (carbonLoading && dashboardTab === DashboardSection.CARBON_AVOIDANCE);

  return (
    <Box>
      {esgError && dashboardTab !== DashboardSection.CARBON_AVOIDANCE && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('dashboard.alerts.esgLoadError', { error: esgError })}
        </Alert>
      )}
      {carbonError && dashboardTab === DashboardSection.CARBON_AVOIDANCE && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('dashboard.alerts.carbonLoadError', { error: carbonError })}
        </Alert>
      )}
      <PageHeader
        icon={<DashboardIcon />}
        eyebrow={t('dashboard.eyebrow')}
        title={t('dashboard.title')}
        subtitle={[
          selectedOrganization?.name,
          dashboardTab === DashboardSection.OVERVIEW
            ? t('dashboard.allTime')
            : dashboardTab === DashboardSection.CARBON_AVOIDANCE
              ? carbonPeriodLabel
              : monthLabel,
        ]
          .filter(Boolean)
          .join(' · ')}
        actions={
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1,
              width: { xs: '100%', md: 'auto' },
            }}
          >
            {(dashboardTab === DashboardSection.MONTHLY ||
              dashboardTab === DashboardSection.SCOPES ||
              dashboardTab === DashboardSection.BRANCHES) && (
              <SetupCoachmark
                open={showMonthTip}
                title={t('selfSetup.tour.dashboardMonthTitle')}
                subtitle={t('selfSetup.tour.dashboardMonthSubtitle')}
                onDismiss={softDismissDashboardTour}
                onNext={advanceDashboardGuide}
                nextLabel={t('selfSetup.tourNext')}
              >
                <Box id="self-setup-dashboard-month" sx={{ flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
                  <DatePicker
                    views={['year', 'month']}
                    openTo="month"
                    value={selectedMonthDate}
                    maxDate={startOfMonth(new Date())}
                    onChange={(newValue) => {
                      if (newValue && isValid(newValue)) {
                        const start = startOfMonth(newValue);
                        handleMonthChange(format(start, "yyyy-MM-dd'T'HH:mm:ss'Z'"));
                      }
                    }}
                    format="MMMM yyyy"
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        sx: { minWidth: { sm: 200 } },
                      },
                    }}
                  />
                </Box>
              </SetupCoachmark>
            )}
            {dashboardTab === DashboardSection.BRANCHES && (
              <SetupCoachmark
                open={showCompareTip}
                title={t('selfSetup.tour.dashboardCompareTitle')}
                subtitle={t('selfSetup.tour.dashboardCompareSubtitle')}
                onDismiss={softDismissDashboardTour}
                onNext={advanceDashboardGuide}
                nextLabel={t('selfSetup.tourNext')}
              >
                <Button
                  id="self-setup-dashboard-compare"
                  variant="outlined"
                  color="inherit"
                  startIcon={<ESG_ICONS.compare fontSize="small" />}
                  onClick={() => {
                    setOpenBranchCompare(true);
                    if (showCompareTip) {
                      syncPhase('compareDialog');
                    }
                  }}
                  disabled={
                    !(showCompareTip || showCompareTourDialog) &&
                    (esgBranchRows.length < 2 || esgLoading)
                  }
                >
                  {t('dashboard.branch.compare.button')}
                </Button>
              </SetupCoachmark>
            )}
            <SetupCoachmark
              open={showKpiTip}
              title={t('selfSetup.tour.dashboardKpiTitle')}
              subtitle={t('selfSetup.tour.dashboardKpiSubtitle')}
              onDismiss={softDismissDashboardTour}
              onNext={advanceDashboardGuide}
              nextLabel={t('selfSetup.tourNext')}
            >
              <Button
                id="self-setup-dashboard-kpi"
                variant="text"
                color="inherit"
                startIcon={<ESG_ICONS.targets fontSize="small" />}
                onClick={handleOpenKPIDialog}
              >
                {t('dashboard.kpiLimits')}
              </Button>
            </SetupCoachmark>
            <SetupCoachmark
              open={showCustomizeButtonTip}
              title={
                helpCustomizeGuide
                  ? tHelp('knowledgeBase.customizeGuideTitle')
                  : t('selfSetup.tour.dashboardTitle')
              }
              subtitle={
                helpCustomizeGuide
                  ? tHelp('knowledgeBase.customizeGuideSubtitle')
                  : t('selfSetup.tour.dashboardSubtitle')
              }
              onDismiss={softDismissDashboardTour}
              onNext={advanceDashboardGuide}
              nextLabel={t('selfSetup.tourNext')}
            >
              <Button
                id="self-setup-dashboard-customize"
                variant="outlined"
                color="inherit"
                startIcon={<ESG_ICONS.customize fontSize="small" />}
                onClick={handleOpenDialog}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {t('dashboard.customize')}
              </Button>
            </SetupCoachmark>
          </Box>
        }
      >
        <Box sx={PAGE_HEADER_TABS_ROW_SX}>
            <Tabs
              id="self-setup-dashboard-tabs"
              value={dashboardTab}
              onChange={(_, value: DashboardSection) => setDashboardTab(value)}
              variant="scrollable"
              scrollButtons="auto"
              sx={PAGE_HEADER_TABS_SX}
            >
              <Tab
                label={
                  showMonthlyTabTip ? (
                    <SetupCoachmark
                      open
                      compact
                      title={t('selfSetup.tour.dashboardTabsTitle')}
                      subtitle={t('selfSetup.tour.dashboardTabsSubtitle')}
                      onDismiss={softDismissDashboardTour}
                      onNext={advanceDashboardGuide}
                      nextLabel={t('selfSetup.tourNext')}
                    >
                      <Box
                        id="self-setup-dashboard-tab-monthly"
                        component="span"
                        sx={{ display: 'inline-block' }}
                      >
                        {t('dashboard.tabs.monthly')}
                      </Box>
                    </SetupCoachmark>
                  ) : (
                    t('dashboard.tabs.monthly')
                  )
                }
              />
              <Tab label={t('dashboard.tabs.overview')} />
              <Tab label={t('dashboard.tabs.scopes')} />
              <Tab
                label={
                  showBranchesTabTip ? (
                    <SetupCoachmark
                      open
                      compact
                      title={t('selfSetup.tour.dashboardBranchesTabTitle')}
                      subtitle={t('selfSetup.tour.dashboardBranchesTabSubtitle')}
                      onDismiss={softDismissDashboardTour}
                      onNext={advanceDashboardGuide}
                      nextLabel={t('selfSetup.tourNext')}
                    >
                      <Box
                        id="self-setup-dashboard-tab-branches"
                        component="span"
                        sx={{ display: 'inline-block' }}
                      >
                        {t('dashboard.tabs.branches')}
                      </Box>
                    </SetupCoachmark>
                  ) : (
                    t('dashboard.tabs.branches')
                  )
                }
              />
              <Tab
                label={
                  showCarbonTabTip ? (
                    <SetupCoachmark
                      open
                      compact
                      title={t('selfSetup.tour.dashboardCarbonTabTitle')}
                      subtitle={t('selfSetup.tour.dashboardCarbonTabSubtitle')}
                      onDismiss={softDismissDashboardTour}
                      onNext={advanceDashboardGuide}
                      nextLabel={t('selfSetup.tourNext')}
                    >
                      <Box
                        id="self-setup-dashboard-tab-carbon"
                        component="span"
                        sx={{ display: 'inline-block' }}
                      >
                        {t('dashboard.tabs.carbonAvoidance')}
                      </Box>
                    </SetupCoachmark>
                  ) : (
                    t('dashboard.tabs.carbonAvoidance')
                  )
                }
              />
            </Tabs>
          </Box>
      </PageHeader>

      {isTabLoading && <DashboardSkeleton variant={skeletonVariant} />}

      {!isTabLoading && dashboardTab === DashboardSection.MONTHLY && (
        <Box sx={{ animation: 'esg-fade-in 200ms ease-out' }}>
          {(() => {
            if (!esgMonthly) {
              return esgError ? null : (
                <EmptyState icon={ESG_ICONS.insights} title={t('common.noData')} minHeight={240} />
              );
            }
            const groups = groupMonthlyCards(orderedMonthlyNumberCards);
            const metricsOf = (items: typeof orderedMonthlyNumberCards) =>
              items.flatMap((item) => (item.kind === 'esg' ? [item.metric] : []));
            // One card per enabled limit (several rules on one KPI each get a card);
            // a KPI with no value this month still shows, as "No data".
            const targetRank = (value: number | null) => (value === null ? 2 : value === 0 ? 1 : 0);
            const kpiTargets =
              hasEnabledKPIs && selectedMonthlyExtras.includes('kpi_progress')
                ? limits
                    .filter((limit) => limit.isEnabled && isTrackableKpi(limit.name))
                    .map((limit) => ({ limit, key: limit.name.toLowerCase(), value: kpiTargetValue(esgMonthly, limit.name) }))
                    .sort((a, b) => targetRank(a.value) - targetRank(b.value))
                : [];

            return (
              <>
                {kpiTargets.length > 0 && (
                  <EsgSection title={t('dashboard.sections.targets')} icon={ESG_ICONS.targets}>
                    <MetricStrip columns={{ xs: 1, sm: 2, md: Math.min(kpiTargets.length, 4) }} dense>
                      {kpiTargets.map(({ limit, key, value }) => (
                        <KPIProgressCard
                          key={limit.id}
                          title={getDisplayTitle(key)}
                          value={value}
                          unit={localizeOpsUnit(KPI_UNITS[key] ?? '')}
                          metricKey={key}
                          limit={limit}
                        />
                      ))}
                    </MetricStrip>
                  </EsgSection>
                )}
                {renderEsgTab(
                  esgMonthly,
                  {
                    performance: metricsOf(groups.performance),
                    avoidedHeadline: metricsOf(groups.avoided).includes(EsgMonthlyMetricType.TOTAL_CO2),
                    avoidedStream: esgMonthlyChartMetrics.includes(EsgMonthlyMetricType.CARBON_AVOIDED_BY_STREAM),
                    inventoryScope1: selectedMonthlyExtras.includes('scope_1'),
                    inventoryScope2: selectedMonthlyExtras.includes('scope_2'),
                    resources: metricsOf(groups.resources),
                    hse: selectedEsgMonthlyMetrics.includes(EsgMonthlyMetricType.HEALTH_SAFETY),
                    social: metricsOf(groups.social),
                    operations: groups.operations.flatMap((item) => (item.kind === 'ops' ? [item.stat] : [])),
                    flowsRecycled: esgMonthlyChartMetrics.includes(EsgMonthlyMetricType.RECYCLED_WASTE_BREAKDOWN),
                    flowsCharts: operationalChartStats,
                    chartKeys: {
                      recycled: EsgMonthlyMetricType.RECYCLED_WASTE_BREAKDOWN,
                      stream: EsgMonthlyMetricType.CARBON_AVOIDED_BY_STREAM,
                    },
                  },
                  'monthly',
                )}
              </>
            );
          })()}
        </Box>
      )}

      {!isTabLoading && dashboardTab === DashboardSection.OVERVIEW && (
        <Box sx={{ animation: 'esg-fade-in 200ms ease-out' }}>
          {!esgOverview && !esgError && (
            <EmptyState icon={ESG_ICONS.insights} title={t('dashboard.alerts.noOverview')} minHeight={240} />
          )}
          {esgOverview && selectedOverviewWidgets.length === 0 && (
            <EmptyState
              icon={ESG_ICONS.customize}
              title={t('dashboard.alerts.noOverviewWidgets')}
              action={customizeAction}
              minHeight={240}
            />
          )}
          {esgOverview &&
            selectedOverviewWidgets.length > 0 &&
            (() => {
              const OVERVIEW_METRIC: Partial<Record<EsgOverviewWidgetId, EsgMonthlyMetricType>> = {
                summary_waste_diverted: EsgMonthlyMetricType.TOTAL_WASTE_DIVERTED,
                summary_diversion_rate: EsgMonthlyMetricType.WASTE_DIVERSION_RATE,
                summary_total_co2: EsgMonthlyMetricType.TOTAL_CO2,
                summary_fill_reports: EsgMonthlyMetricType.FILL_LEVEL_REPORT_COUNT,
                summary_training_hours: EsgMonthlyMetricType.TRAINING_HOURS_TOTAL,
                utility_water: EsgMonthlyMetricType.WATER_USE,
                utility_electricity: EsgMonthlyMetricType.ELECTRICITY_USE,
                utility_oil: EsgMonthlyMetricType.OIL_USE,
                utility_gas: EsgMonthlyMetricType.GAS_USE,
              };
              const metricsIn = (section: Parameters<typeof overviewWidgetsIn>[0]) =>
                overviewWidgetsIn(section, selectedOverviewWidgets).flatMap((id) =>
                  OVERVIEW_METRIC[id] ? [OVERVIEW_METRIC[id] as EsgMonthlyMetricType] : [],
                );
              const hasIncoming =
                selectedOverviewSet.has('chart_incoming') &&
                Array.isArray(esgOverview.incoming_waste_breakdown) &&
                esgOverview.incoming_waste_breakdown.length > 0;
              return renderEsgTab(
                esgOverview,
                {
                  performance: metricsIn('performance'),
                  avoidedHeadline: selectedOverviewSet.has('summary_total_co2'),
                  avoidedStream: selectedOverviewSet.has('chart_carbon_stream'),
                  inventoryScope1: selectedOverviewSet.has('scope_1'),
                  inventoryScope2: selectedOverviewSet.has('scope_2'),
                  resources: metricsIn('resources'),
                  hse: selectedOverviewSet.has('summary_health_safety'),
                  social: metricsIn('social'),
                  operations: [],
                  flowsRecycled:
                    selectedOverviewSet.has('chart_recycled') &&
                    (esgOverview.waste_type_material_breakdown?.length ?? 0) > 0,
                  flowsCharts: hasIncoming ? [StatisticType.INCOMING_WASTE_BREAKDOWN] : [],
                  chartKeys: { recycled: 'esg_overview_breakdown', stream: 'esg_overview_carbon_stream' },
                },
                'overview',
              );
            })()}
        </Box>
      )}

      {!isTabLoading && dashboardTab === DashboardSection.SCOPES && (
        <Box sx={{ animation: 'esg-fade-in 200ms ease-out' }}>
          {esgMonthly ? (
            selectedScopesWidgets.length === 0 ? (
              <EmptyState
                icon={ESG_ICONS.customize}
                title={t('dashboard.alerts.noTabWidgets')}
                action={customizeAction}
                minHeight={240}
              />
            ) : (
              <EsgSection
                title={t('dashboard.sections.ghgInventory')}
                icon={ESG_ICONS.ghgInventory}
                description={t('dashboard.sectionDescriptions.ghgInventory')}
                meta={periodChip(monthLabel)}
              >
                <ScopeEmissionsPanel
                  summary={esgMonthly.scope_emissions}
                  showBreakdown={selectedScopesWidgets.includes('breakdown')}
                  showScope1={selectedScopesWidgets.includes('scope_1')}
                  showScope2={selectedScopesWidgets.includes('scope_2')}
                  context={monthLabel}
                />
              </EsgSection>
            )
          ) : (
            <EmptyState icon={ESG_ICONS.ghgInventory} title={t('dashboard.inventory.noData')} minHeight={240} />
          )}
        </Box>
      )}

      {!isTabLoading && dashboardTab === DashboardSection.BRANCHES && (
        <Box sx={{ animation: 'esg-fade-in 200ms ease-out' }}>
          <SetupCoachmark
            open={showBranchesTip}
            title={t('selfSetup.tour.dashboardBranchesTitle')}
            subtitle={t('selfSetup.tour.dashboardBranchesSubtitle')}
            onDismiss={softDismissDashboardTour}
            onNext={advanceDashboardGuide}
            nextLabel={t('selfSetup.tourNext')}
            fullWidth
            placement="top"
          >
            <Box id="self-setup-dashboard-branches">
              <EsgSection
                title={t('dashboard.sections.branches')}
                icon={ESG_ICONS.branches}
                description={t('dashboard.sectionDescriptions.branches')}
                meta={periodChip(monthLabel)}
                actions={
                  esgBranchRows.length > 1 && selectedBranchesWidgets.length > 0 ? (
                    <Button
                      size="small"
                      color="inherit"
                      startIcon={allBranchesExpanded ? <UnfoldLessRounded /> : <UnfoldMoreRounded />}
                      onClick={() =>
                        setExpandedBranches(
                          allBranchesExpanded
                            ? new Set()
                            : new Set(esgBranchRows.map((row) => row.collection_site_id)),
                        )
                      }
                    >
                      {allBranchesExpanded ? t('dashboard.branch.collapseAll') : t('dashboard.branch.expandAll')}
                    </Button>
                  ) : undefined
                }
                sx={{ mb: 2 }}
              >
                {esgByBranch && esgBranchRows.length === 0 && (
                  <EmptyState icon={ESG_ICONS.branches} title={t('dashboard.alerts.noBranchData')} minHeight={220} />
                )}
                {esgBranchRows.length > 0 && selectedBranchesWidgets.length === 0 && (
                  <EmptyState
                    icon={ESG_ICONS.customize}
                    title={t('dashboard.alerts.noTabWidgets')}
                    action={customizeAction}
                    minHeight={220}
                  />
                )}
                {esgBranchRows.length > 0 && selectedBranchesWidgets.length > 0 && (
                  <Box
                    sx={{
                      display: 'grid',
                      gap: { xs: 2, md: 2.5 },
                      gridTemplateColumns: 'minmax(0, 1fr)',
                      alignItems: 'stretch',
                    }}
                  >
                    {esgBranchRows.map((row) => (
                      <BranchSiteCard
                        key={row.collection_site_id}
                        row={row}
                        orgTrainingHours={esgByBranch?.training_hours_total}
                        formatSar={formatSar}
                        visibleWidgets={selectedBranchesWidgets}
                        highlightHole={showBranchesTip}
                        expanded={expandedBranches.has(row.collection_site_id)}
                        onToggle={() =>
                          setExpandedBranches((prev) => {
                            const next = new Set(prev);
                            if (next.has(row.collection_site_id)) next.delete(row.collection_site_id);
                            else next.add(row.collection_site_id);
                            return next;
                          })
                        }
                      />
                    ))}
                  </Box>
                )}
              </EsgSection>
            </Box>
          </SetupCoachmark>
        </Box>
      )}

      {!isTabLoading && dashboardTab === DashboardSection.CARBON_AVOIDANCE && (
        <Box sx={{ animation: 'esg-fade-in 200ms ease-out' }}>
          <Box
            sx={{
              mb: { xs: 3, md: 4 },
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              columnGap: 2,
              rowGap: 1.5,
            }}
          >
            <SetupCoachmark
              open={showCarbonDateTip}
              title={t('selfSetup.tour.dashboardCarbonDateTitle')}
              subtitle={t('selfSetup.tour.dashboardCarbonDateSubtitle')}
              onDismiss={softDismissDashboardTour}
              onNext={advanceDashboardGuide}
              nextLabel={t('selfSetup.tourNext')}
            >
              <Box
                id="self-setup-dashboard-carbon-date"
                sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}
              >
                <DatePicker
                  label={t('dashboard.startDate')}
                  value={carbonPeriod.startDate}
                  maxDate={carbonPeriod.endDate}
                  onChange={(newValue) => {
                    if (newValue && isValid(newValue)) {
                      updateCarbonPeriod({ startDate: startOfDay(newValue) });
                    }
                  }}
                  format="MMM d, yyyy"
                  slotProps={{ textField: { size: 'small', sx: { width: { xs: '100%', sm: 180 } } } }}
                />
                <DatePicker
                  label={t('dashboard.endDate')}
                  value={carbonPeriod.endDate}
                  minDate={carbonPeriod.startDate}
                  maxDate={carbonToday}
                  onChange={(newValue) => {
                    if (newValue && isValid(newValue)) {
                      updateCarbonPeriod({ endDate: startOfDay(newValue) });
                    }
                  }}
                  format="MMM d, yyyy"
                  slotProps={{ textField: { size: 'small', sx: { width: { xs: '100%', sm: 180 } } } }}
                />
              </Box>
            </SetupCoachmark>
            <Typography variant="caption" color="text.secondary" sx={{ marginInlineStart: { sm: 'auto' } }}>
              {t('dashboard.basedOnDeliveryDate')}
            </Typography>
          </Box>

          {!carbonPeriodValid && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              {t('dashboard.alerts.carbonDateWarning')}
            </Alert>
          )}

          <SetupCoachmark
            open={showCarbonContentTip}
            title={t('selfSetup.tour.dashboardCarbonContentTitle')}
            subtitle={t('selfSetup.tour.dashboardCarbonContentSubtitle')}
            onDismiss={softDismissDashboardTour}
            onNext={advanceDashboardGuide}
            nextLabel={t('selfSetup.tourDone')}
            fullWidth
          >
            <Box id="self-setup-dashboard-carbon-content">
              {carbonReport && carbonPeriodValid && !carbonLoading && (
                selectedCarbonWidgets.length === 0 ? (
                  <EmptyState
                    icon={ESG_ICONS.customize}
                    title={t('dashboard.alerts.noTabWidgets')}
                    action={customizeAction}
                    minHeight={220}
                  />
                ) : (
                  <CarbonAvoidanceDetailView
                    report={carbonReport}
                    periodLabel={carbonPeriodLabel}
                    visibleWidgets={selectedCarbonWidgets}
                  />
                )
              )}

              {!carbonReport && !carbonLoading && carbonPeriodValid && !carbonError && (
                <EmptyState icon={ESG_ICONS.avoidedEmissions} title={t('dashboard.alerts.noCarbonData')} minHeight={220} />
              )}
            </Box>
          </SetupCoachmark>
        </Box>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleChartTypeChange('bar')}>
          <ListItemIcon>
            <BarChartIcon fontSize="small" sx={{ color: "text.secondary" }} />
          </ListItemIcon>
          <ListItemText>{t('dashboard.chartTypes.bar')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleChartTypeChange('line')}>
          <ListItemIcon>
            <LineChartIcon fontSize="small" sx={{ color: "text.secondary" }} />
          </ListItemIcon>
          <ListItemText>{t('dashboard.chartTypes.line')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleChartTypeChange('area')}>
          <ListItemIcon>
            <AreaChartIcon fontSize="small" sx={{ color: "text.secondary" }} />
          </ListItemIcon>
          <ListItemText>{t('dashboard.chartTypes.area')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleChartTypeChange('pie')}>
          <ListItemIcon>
            <PieChartIcon fontSize="small" sx={{ color: "text.secondary" }} />
          </ListItemIcon>
          <ListItemText>{t('dashboard.chartTypes.pie')}</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        hideBackdrop={
          showCustomizeSearchTip ||
          showCustomizeMetricsTip ||
          showCustomizeSaveTip
        }
        PaperProps={{ sx: { overflow: 'hidden' } }}
      >
        <DialogTitle
          component="div"
          sx={{
            px: 3,
            pt: 2.5,
            pb: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1.5,
            }}
          >
            <Box sx={{ minWidth: 0, pr: 1 }}>
              <Typography
                variant="h6"
                component="h2"
                sx={{ fontWeight: 600, fontSize: '1.125rem', lineHeight: 1.3, color: 'text.primary' }}
              >
                {customizeDialogCopy.title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mt: 0.75,
                  color: 'text.secondary',
                  lineHeight: 1.55,
                  fontWeight: 400,
                  maxWidth: 440,
                }}
              >
                {customizeDialogCopy.hint}
              </Typography>
            </Box>
            <IconButton
              aria-label={t('common.close')}
              onClick={handleCloseDialog}
              size="small"
              sx={{ mt: -0.5, color: 'text.secondary', '&:hover': { color: 'text.primary' } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            maxHeight: '55vh',
            px: 3,
            pt: 2.5,
            pb: 2,
          }}
        >
          <Box id="help-customize-dialog">
          <Box sx={{ mb: 2 }}>
            <SetupCoachmark
              open={showCustomizeSearchTip}
              title={t('selfSetup.tour.dashboardSearchTitle')}
              subtitle={t('selfSetup.tour.dashboardSearchSubtitle')}
              onDismiss={softDismissDashboardTour}
              onNext={advanceDashboardGuide}
              nextLabel={t('selfSetup.tourNext')}
              fullWidth
            >
              <TextField
                id="self-setup-dashboard-search"
                fullWidth
                size="small"
                placeholder={t('dashboard.searchWidgets')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <Box sx={{ display: 'flex', marginInlineEnd: 1, color: 'text.secondary' }}>
                      <SearchIcon fontSize="small" />
                    </Box>
                  ),
                }}
              />
            </SetupCoachmark>
          </Box>

          <SetupCoachmark
            open={showCustomizeMetricsTip}
            title={t('selfSetup.tour.dashboardMetricsTitle')}
            subtitle={t('selfSetup.tour.dashboardMetricsSubtitle')}
            onDismiss={softDismissDashboardTour}
            onNext={advanceDashboardGuide}
            nextLabel={t('selfSetup.tourNext')}
            fullWidth
          >
            <Box id="self-setup-dashboard-metrics">
              {dashboardTab === DashboardSection.MONTHLY && (
                <DashboardCustomizeWidgetList
                  items={monthlyCustomizeItems}
                  selectedIds={monthlyCustomizeSelected}
                  searchQuery={searchQuery}
                  selectAllLabel={t('dashboard.selectAllMetrics')}
                  onChange={(next) => {
                    const extras = ESG_MONTHLY_EXTRA_WIDGETS.filter((id) => next.includes(id));
                    const metrics = PICKABLE_ESG_MONTHLY_METRICS.filter((id) => next.includes(id));
                    const ops = operationalStatsForOrg.filter((id) => next.includes(id));
                    setSelectedMonthlyExtras(extras);
                    setSelectedEsgMonthlyMetrics(metrics);
                    setSelectedStats(ops);
                    sessionStorage.setItem('esgMonthlySelectedExtras', JSON.stringify(extras));
                    sessionStorage.setItem('esgMonthlySelectedMetrics', JSON.stringify(metrics));
                    if (orgType) persistOperationalStatsForOrg(orgType, ops);
                  }}
                />
              )}
              {dashboardTab === DashboardSection.OVERVIEW && (
                <DashboardCustomizeWidgetList
                  items={PICKABLE_ESG_OVERVIEW_WIDGETS.map((id) => ({
                    id,
                    label: getOverviewWidgetLabel(t, id),
                  }))}
                  selectedIds={selectedOverviewWidgets}
                  searchQuery={searchQuery}
                  selectAllLabel={t('dashboard.selectAllOverviewWidgets')}
                  onChange={(next) => {
                    const widgets = PICKABLE_ESG_OVERVIEW_WIDGETS.filter((id) => next.includes(id));
                    setSelectedOverviewWidgets(widgets);
                    sessionStorage.setItem('esgOverviewSelectedWidgets', JSON.stringify(widgets));
                  }}
                />
              )}
              {dashboardTab === DashboardSection.SCOPES && (
                <DashboardCustomizeWidgetList
                  items={scopesCustomizeItems}
                  selectedIds={selectedScopesWidgets}
                  searchQuery={searchQuery}
                  selectAllLabel={t('dashboard.selectAllMetrics')}
                  onChange={(next) => {
                    const widgets = ESG_SCOPES_WIDGETS.filter((id) => next.includes(id));
                    setSelectedScopesWidgets(widgets);
                    sessionStorage.setItem('esgScopesSelectedWidgets', JSON.stringify(widgets));
                  }}
                />
              )}
              {dashboardTab === DashboardSection.BRANCHES && (
                <DashboardCustomizeWidgetList
                  items={branchesCustomizeItems}
                  selectedIds={selectedBranchesWidgets}
                  searchQuery={searchQuery}
                  selectAllLabel={t('dashboard.selectAllMetrics')}
                  onChange={(next) => {
                    const widgets = ESG_BRANCHES_WIDGETS.filter((id) => next.includes(id));
                    setSelectedBranchesWidgets(widgets);
                    sessionStorage.setItem('esgBranchesSelectedWidgets', JSON.stringify(widgets));
                  }}
                />
              )}
              {dashboardTab === DashboardSection.CARBON_AVOIDANCE && (
                <DashboardCustomizeWidgetList
                  items={carbonCustomizeItems}
                  selectedIds={selectedCarbonWidgets}
                  searchQuery={searchQuery}
                  selectAllLabel={t('dashboard.selectAllMetrics')}
                  onChange={(next) => {
                    const widgets = ESG_CARBON_WIDGETS.filter((id) => next.includes(id));
                    setSelectedCarbonWidgets(widgets);
                    sessionStorage.setItem('esgCarbonSelectedWidgets', JSON.stringify(widgets));
                  }}
                />
              )}
            </Box>
          </SetupCoachmark>
          </Box>
        </DialogContent>

        {/* Actions */}
        <DialogActions
          sx={{
            p: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button onClick={handleCloseDialog} color="inherit">
            {t('common.cancel')}
          </Button>
          <SetupCoachmark
            open={showCustomizeSaveTip}
            title={t('selfSetup.tour.dashboardSaveTitle')}
            subtitle={t('selfSetup.tour.dashboardSaveSubtitle')}
            onDismiss={softDismissDashboardTour}
            onNext={advanceDashboardGuide}
            nextLabel={
              showOverviewTour && overviewTourPhase === 'customizeSave'
                ? t('selfSetup.tourNext')
                : t('selfSetup.tourDone')
            }
          >
            <Button
              id="self-setup-dashboard-save"
              onClick={handleSavePreferences}
              variant="contained"
            >
              {t('dashboard.saveChanges')}
            </Button>
          </SetupCoachmark>
        </DialogActions>
      </Dialog>

      <KPILimitsDialog
        open={openKPIDialog || showKpiFormTip || showKpiListTip}
        onClose={() => {
          if (showKpiFormTip || showKpiListTip) {
            softDismissDashboardTour();
            return;
          }
          handleCloseKPIDialog();
        }}
        tourTip={showKpiFormTip ? 'kpiForm' : showKpiListTip ? 'kpiList' : null}
        onTourNext={advanceDashboardGuide}
        onTourDismiss={softDismissDashboardTour}
        tourNextLabel={
          helpDashGuide === 'kpi' && showKpiListTip
            ? t('selfSetup.tourDone')
            : t('selfSetup.tourNext')
        }
      />

      <BranchComparisonDialog
        open={openBranchCompare || showCompareTourDialog}
        onClose={() => {
          if (showCompareTourDialog) {
            softDismissDashboardTour();
            return;
          }
          setOpenBranchCompare(false);
        }}
        branches={esgBranchRows}
        tourTip={
          showCompareResultsTip
            ? 'results'
            : showCompareDialogTip
              ? 'select'
              : null
        }
        onTourNext={advanceDashboardGuide}
        onTourDismiss={softDismissDashboardTour}
        tourTitle={
          showCompareResultsTip
            ? t('selfSetup.tour.dashboardCompareResultsTitle')
            : t('selfSetup.tour.dashboardCompareDialogTitle')
        }
        tourSubtitle={
          showCompareResultsTip
            ? t('selfSetup.tour.dashboardCompareResultsSubtitle')
            : t('selfSetup.tour.dashboardCompareDialogSubtitle')
        }
        tourNextLabel={
          helpDashGuide === 'branches' && showCompareResultsTip
            ? t('selfSetup.tourDone')
            : t('selfSetup.tourNext')
        }
      />
    </Box>
  );
};

export default Dashboard;
